/**
 * 全局语音状态管理（Zustand）
 *
 * 职责：
 *   1. 桥接 VoiceRuntime（全局 VAD）与页面（如 AiVoicePage）之间的通信
 *   2. VoiceRuntime 将 VAD 状态/操作注册到 store，页面通过 store 消费
 *   3. 管理对话消息
 */

import { create } from "zustand";
import type { DeviceStatus, VadState, MicErrorType } from "@/voice/types";
import type { Msg } from "@/pages/ai-voice/types";

type ConversationDraftMessage = Omit<Msg, "id"> & { id?: number };

// ─── 模块级消息去重 Set（不在 Zustand 响应式 state 内，避免克隆开销）──
let __seenMessageHashes = new Set<string>();

/** 麦克风错误信息 */
export interface MicError {
  errorType: MicErrorType;
  message: string;
  /** 错误发生时间 */
  timestamp: number;
}

interface VoiceStoreState {
  // ─── VAD 运行时状态（由 VoiceRuntime 同步写入） ────

  /** VAD 状态机：idle / recording / speaking / silence_detecting / press_recording */
  vadState: VadState;
  /** 是否正在监听麦克风（recording || speaking || silence_detecting || press_recording） */
  isListening: boolean;
  /** 是否处于按住录音模式 */
  isPressRecording: boolean;
  /** 设备检测状态：checking / available / unavailable / denied */
  deviceStatus: DeviceStatus;
  /** 实时波形数据（AnalyserNode 时域数据，0-255，128=静音中线） */
  waveData: Uint8Array;
  /** 原始音频是否静音（振幅过低，波形不显示） */
  audioSilent: boolean;
  /** 录音 Blob 已输出，等待处理完成 */
  isAudioProcessing: boolean;
  /** TTS 语音正在播放中（控制波纹效果） */
  isTtsPlaying: boolean;
  setIsTtsPlaying: (playing: boolean) => void;
  /** 是否已自动启动监听 */
  autoStarted: boolean;
  /** 麦克风错误信息（权限拒绝/设备不可用等） */
  micError: MicError | null;
  /** 设置麦克风错误（供 UI 展示提示） */
  setMicError: (error: MicError | null) => void;
  /**
   * 用户是否希望保持「常驻对讲」开启。
   * 在 ai-voice 等页面停止/切文字输入时应设为 false，避免悬浮层或其它逻辑立刻再次 startListening。
   */
  voicePersistEnabled: boolean;
  setVoicePersistEnabled: (enabled: boolean) => void;

  // ─── VAD 操作（由 VoiceRuntime 注册，页面可调用） ──

  /** 开始监听麦克风（VAD 自动检测模式） */
  startListening: () => Promise<void>;
  /** 停止监听并释放麦克风 */
  stopListening: () => void;
  /** ★ 按住录音开始（微信式：按住即录）*/
  startPressToRecord: () => Promise<boolean>;
  /** ★ 按住录音结束（松手即停，cancelled=true 表示取消）。返回 true 表示确实进入了录音停止流程 */
  stopPressToRecord: (cancelled?: boolean) => Promise<boolean>;
  /** 外部处理完成后调用，重置 isAudioProcessing */
  finishAudioProcessing: () => void;

  // ─── 内部方法 ───────────────────────────────────────

  /** VoiceRuntime 批量同步 VAD 状态 */
  setVadRuntimeState: (
    partial: Partial<
      Pick<
        VoiceStoreState,
        | "vadState"
        | "isListening"
        | "isPressRecording"
        | "deviceStatus"
        | "waveData"
        | "audioSilent"
        | "isAudioProcessing"
      >
    >,
  ) => void;
  /** VoiceRuntime 注册 VAD 操作，返回取消注册函数 */
  registerVadActions: (
    actions: Pick<
      VoiceStoreState,
      | "startListening"
      | "stopListening"
      | "startPressToRecord"
      | "stopPressToRecord"
      | "finishAudioProcessing"
    >,
  ) => () => void;
  /** VoiceRuntime 标记自动启动状态 */
  setAutoStarted: (started: boolean) => void;

  // ─── 对话消息 ───────────────────────────────────────

  /** 全局语音会话消息（内存态） */
  conversationMessages: Msg[];
  /** 消息 ID 自增器 */
  nextMessageId: number;
  /** 当前等待替换的占位消息 ID（发送消息时设置，收到回复时删除） */
  pendingTypingMsgId: number | null;
  /** 设置占位消息 ID */
  setPendingTypingMsgId: (id: number | null) => void;
  /** 追加会话消息（自动分配递增 id） */
  appendConversationMessages: (messages: ConversationDraftMessage[]) => Msg[];
  /** 前插会话消息到列表头部（用于加载历史消息，旧消息在上，新消息在下） */
  prependConversationMessages: (messages: ConversationDraftMessage[]) => Msg[];
  /** 按 id 更新会话中的某条消息 */
  updateConversationMessage: (id: number, patch: Partial<Msg>) => void;
  /** 按 id 删除会话中的某条消息 */
  removeConversationMessage: (id: number) => void;
  /** 清空会话 */
  resetConversation: () => void;
  /** ★ 消息去重：添加 hash（SSE 推送后调用） */
  addSeenMessageHash: (hash: string) => void;
  /** ★ 消息去重：检查 hash 是否存在 */
  hasSeenMessageHash: (hash: string) => boolean;
}

export const useVoiceStore = create<VoiceStoreState>((set, get) => ({
  // ─── 初始值 ─────────────────────────────────────────
  vadState: "idle",
  isListening: false,
  isPressRecording: false,
  deviceStatus: "checking",
  waveData: new Uint8Array(0),
  audioSilent: true,
  isAudioProcessing: false,
  isTtsPlaying: false,
  setIsTtsPlaying: (playing) => set({ isTtsPlaying: playing }),
  autoStarted: false,
  micError: null,
  /** ★ 已改为按住说话模式，默认关闭常驻对讲 */
  voicePersistEnabled: false,
  conversationMessages: [],
  nextMessageId: 0,
  pendingTypingMsgId: null,
  setPendingTypingMsgId: (id) => set({ pendingTypingMsgId: id }),

  // VAD 操作占位（VoiceRuntime 挂载前为空操作）
  startListening: async () => undefined,
  stopListening: () => undefined,
  startPressToRecord: async () => false,
  stopPressToRecord: async () => false,
  finishAudioProcessing: () => undefined,

  // ─── 内部方法实现 ───────────────────────────────────

  setVadRuntimeState: (partial) => set(partial),

  registerVadActions: (actions) => {
    set(actions);
    return () => {
      const state = get();
      if (
        state.startListening === actions.startListening &&
        state.stopListening === actions.stopListening &&
        state.startPressToRecord === actions.startPressToRecord &&
        state.stopPressToRecord === actions.stopPressToRecord &&
        state.finishAudioProcessing === actions.finishAudioProcessing
      ) {
        set({
          startListening: async () => undefined,
          stopListening: () => undefined,
          startPressToRecord: async () => false,
          stopPressToRecord: async () => false,
          finishAudioProcessing: () => undefined,
        });
      }
    };
  },

  setAutoStarted: (started) => set({ autoStarted: started }),
  setVoicePersistEnabled: (enabled) => set({ voicePersistEnabled: enabled }),
  setMicError: (error) => set({ micError: error ? { ...error, timestamp: Date.now() } : null }),

  appendConversationMessages: (messages) => {
    if (messages.length === 0) return [];

    let appended: Msg[] = [];
    set((state) => {
      let cursor = state.nextMessageId;
      appended = messages.map((message) => {
        const { id, ...rest } = message;
        if (typeof id === "number") {
          cursor = Math.max(cursor, id + 1);
          return { ...rest, id };
        }
        const next = { ...rest, id: cursor };
        cursor += 1;
        return next;
      });

      const arr = [...state.conversationMessages, ...appended];
      return {
        conversationMessages: arr,
        nextMessageId: cursor,
      };
    });

    return appended;
  },

  prependConversationMessages: (messages) => {
    if (messages.length === 0) return [];

    let prepended: Msg[] = [];
    set((state) => {
      // ★ 历史消息使用负数 ID，确保不与 append 的正数 ID 冲突
      const baseId = state.conversationMessages.length > 0
        ? Math.min(0, state.conversationMessages[0].id) - messages.length
        : -messages.length;

      prepended = messages.map((message, i) => {
        const { id, ...rest } = message;
        return { ...rest, id: typeof id === "number" ? id : baseId + i };
      });

      return {
        conversationMessages: [...prepended, ...state.conversationMessages],
      };
    });

    return prepended;
  },

  updateConversationMessage: (id, patch) =>
    set((state) => ({
      conversationMessages: state.conversationMessages.map((message) =>
        message.id === id ? { ...message, ...patch } : message,
      ),
    })),

  removeConversationMessage: (id) =>
    set((state) => ({
      conversationMessages: state.conversationMessages.filter(
        (message) => message.id !== id,
      ),
    })),

  resetConversation: () => {
    __seenMessageHashes = new Set<string>();
    set({
      conversationMessages: [],
      nextMessageId: 0,
    });
  },

  addSeenMessageHash: (hash) => { __seenMessageHashes.add(hash); },
  hasSeenMessageHash: (hash) => __seenMessageHashes.has(hash),
}));
